import clsx from 'clsx';
import { Slider } from 'radix-ui';
import { type ReactNode, useId } from 'react';
import { LuEye, LuEyeClosed, LuInfo } from 'react-icons/lu';

import { ToggleSwitch, TooltipContent, TooltipRoot, TooltipTrigger } from '~components';
import { Button } from '~components/ui/button';
import { Spinner } from '~components/ui/spinner';
import { useDebounce } from '~hooks';
import type { LayerVisibility } from '../hooks';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type SwitchOpacityProps = {
  /** Current visibility state of the layer */
  isVisible: boolean;
  /** Callback when visibility changes, receives new visibility state */
  onToggle: (isVisible: boolean) => void;
  /** Whether the switch is disabled (e.g. when loading or in error state) */
  isDisabled?: boolean;
  /** Label text rendered inside <label> for the switch */
  children?: ReactNode;
  /** Element rendered between the label and the switch (e.g. spinner, error icon) — outside <label> to avoid triggering the switch */
  accessory?: ReactNode;
};

type SliderOpacityProps = {
  /** Optional label displayed above the slider */
  label?: string;
  /** Current opacity value (0-1) */
  value: number;
  /** Callback when the slider value changes, receives new opacity (0-1) */
  onValueChange: (value: number) => void;
  /** Whether the slider is disabled (e.g. when loading or in error state) */
  isDisabled?: boolean;
};

type LayerItemProps = {
  /** Display label for the layer */
  label: string;
  /** Visibility and opacity state from useLayerVisibility */
  layerVisibility: LayerVisibility;
  /** Shows a loading spinner on the row */
  isLoading?: boolean;
  /** Shows an inline error state — disables the switch when true */
  isError?: boolean;
  /** Error message shown as a tooltip or subtitle below the label */
  errorMessage?: string;
};

/* -------------------------------------------------------------------------- */
/*                               Sub-components                               */
/* -------------------------------------------------------------------------- */

/**
 * On/off switch with a label and an optional accessory slot.
 *
 * @example
 * ```tsx
 *   <SwitchOpacity
 *     isVisible={layerVisibility.isVisible}
 *     onToggle={layerVisibility.setVisible}
 *     isDisabled={isLoading || isError}
 *     accessory={
 *       <>
 *         {isLoading && <Spinner className="size-5 p-0" />}
 *         {isError && errorMessage && <ErrorTooltip message={errorMessage} />}
 *       </>
 *     }
 *   >
 *     {label}
 *   </SwitchOpacity>
 * ```
 */
const SwitchOpacity = ({ isVisible, isDisabled, onToggle, children, accessory }: SwitchOpacityProps) => {
  const id = useId();

  return (
    <div className="group/switch flex items-center justify-between gap-2">
      <label
        htmlFor={id}
        className={clsx('flex items-center gap-1 text-sm leading-none transition-colors duration-200 ease-in-out', {
          'group-hover/switch:text-primary': !isDisabled,
          'text-muted-foreground': isDisabled,
        })}
      >
        {children}
        {accessory}
      </label>
      <ToggleSwitch
        id={id}
        checked={isVisible}
        onCheckedChange={onToggle}
        disabled={isDisabled}
        ariaLabel={typeof children === 'string' ? `${children} visibility` : 'Layer visibility'}
      />
    </div>
  );
};

/**
 * Opacity slider with an eye icon that reflects the current value.
 * Expects a controlled value in the 0–1 range.
 *
 * @example
 * ```tsx
 *   <SliderOpacity
 *     label={label}
 *     value={liveOpacity}
 *     onValueChange={setLiveOpacity}
 *     isDisabled={isLoading || isError}
 *   />
 * ```
 */
const SliderOpacity = ({ label, value, onValueChange, isDisabled }: SliderOpacityProps) => {
  const id = useId();

  return (
    <div className="flex items-center gap-2">
      {value > 0 ? (
        <LuEye size={16} className="shrink-0 text-muted-foreground" aria-hidden="true" />
      ) : (
        <LuEyeClosed size={16} className="shrink-0 text-muted-foreground" aria-hidden="true" />
      )}
      <Slider.Root
        name={id}
        className="relative flex h-1 w-full touch-none select-none items-center"
        value={[value]}
        onValueChange={([newValue]) => onValueChange(newValue)}
        disabled={isDisabled}
        min={0}
        max={1}
        step={0.01}
      >
        <Slider.Track className="relative h-1 grow rounded-full bg-border">
          <Slider.Range className="absolute h-full rounded-full bg-primary" />
        </Slider.Track>
        <Slider.Thumb
          className={clsx(
            'block size-4 cursor-grab rounded-full border border-border bg-background shadow-md',
            'focus:outline-0 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 active:cursor-grabbing',
          )}
          aria-label={label ? `${label} opacity` : 'Layer opacity'}
        />
      </Slider.Root>
      <span className="text-muted-foreground text-xs">{Math.round(value * 100)}%</span>
    </div>
  );
};

/**
 * Error info icon with a tooltip showing the error message.
 * Expects a Tooltip.Provider ancestor.
 */
const ErrorTooltip = ({ message }: { message: string }) => (
  <TooltipRoot>
    <TooltipTrigger asChild>
      <Button icon variant="ghost" size="sm" className="size-5">
        <LuInfo className="size-4" color="var(--color-destructive)" aria-label="Error details" />
      </Button>
    </TooltipTrigger>
    <TooltipContent>{message}</TooltipContent>
  </TooltipRoot>
);

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * UI component for controlling the visibility and opacity of a single layer, with built-in loading and error states.
 * Expects a `Tooltip.Provider` ancestor (typically in `App.tsx`).
 *
 * @example
 * ```tsx
 *   const blastLayerVisibility = useLayerVisibility({ visible: true, opacity: 0.8 });
 *
 *   <LayerItem
 *     label="Blast domains"
 *     layerVisibility={blastLayerVisibility}
 *     isLoading={isLoadingBlastData}
 *     isError={hasBlastDataError}
 *     errorMessage={blastDataErrorMessage}
 *   />
 * ```
 */
const LayerItem = ({ label, layerVisibility, isLoading, isError, errorMessage }: LayerItemProps) => {
  const isDisabled = isLoading || isError;

  const [liveOpacity, setLiveOpacity] = useDebounce(layerVisibility.opacity, layerVisibility.setOpacity, 100);

  return (
    <div className={clsx('flex flex-col py-3', { 'pointer-events-none opacity-30': isLoading })}>
      <SwitchOpacity
        isVisible={layerVisibility.isVisible}
        onToggle={layerVisibility.setVisible}
        isDisabled={isDisabled}
        accessory={
          <>
            {isLoading && <Spinner label={`${label} layer loading`} />}
            {isError && errorMessage && <ErrorTooltip message={errorMessage} />}
          </>
        }
      >
        {label}
      </SwitchOpacity>

      {/* Opacity slider — only when visible and no error */}
      <div
        className={clsx(
          'mt-3 starting:opacity-0 transition-[opacity,display] transition-discrete duration-300 ease-in-out',
          layerVisibility.isVisible && !isError ? 'block opacity-100' : 'hidden opacity-0',
        )}
      >
        <SliderOpacity label={label} value={liveOpacity} onValueChange={setLiveOpacity} isDisabled={isDisabled} />
      </div>
    </div>
  );
};
LayerItem.displayName = 'LayerItem';

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export { LayerItem };
